import { ChangeDetectionStrategy, Component, PLATFORM_ID, computed, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { CardPreauthStore } from './card-preauth.store';
import { noticeShortDate } from './card-preauth.models';
import { cardPreauthText } from './card-preauth-texts';
import { promoText } from './promo-texts';

/**
 * Subscription toast, fixed bottom-right (bottom sheet on narrow screens). Shown in
 * `tomorrow` once per session and in `expired` on every app entry — the rules live in
 * CardPreauthStore.openToastIfDue. Client-only: nothing renders during SSR, so there
 * is no hydration mismatch.
 *
 * The same component carries the free-days promo variant (`promo()`): one toast, two
 * copies, with the card-confirmation notice taking precedence whenever both would be due
 * (CardPreauthStore.promoNotice is false while `showBanner` is true).
 */
@Component({
    selector: 'card-preauth-toast',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './card-preauth-toast.component.html',
    styleUrls: ['./card-preauth-toast.component.scss'],
})
export class CardPreauthToastComponent {
    private readonly store = inject(CardPreauthStore);
    private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    // The card-confirmation toast yields to a free-days offer: activating the offer registers
    // the card as well, so the promo variant is the one message worth showing.
    private readonly cardToastDue = computed(() =>
        !this.store.promoNotice() && this.store.toastOpen() && ['tomorrow', 'expired'].includes(this.store.phase()));

    /** The promo variant: the offer exists and the once-per-session toast is open. */
    readonly promo = computed(() => this.store.promoToastOpen() && this.store.promoNotice());

    readonly expired = computed(() => !this.promo() && this.store.phase() === 'expired');

    readonly visible = computed(() => this.isBrowser && (this.cardToastDue() || this.promo()));

    readonly title = computed(() => {
        if (this.promo()) {
            return promoText('payment.promo.toast.title', [String(this.store.promoBenefit()?.headline.days ?? 0)]);
        }
        const expiresAt = this.store.expiresAt();
        if (this.expired()) {
            return cardPreauthText('payment.card_preauth.toast.expired_title', [expiresAt ? noticeShortDate(expiresAt) : '']).trim();
        }
        // Same wording as the header pill: "Pretplata ističe" + "sutra" / "danas".
        const day = cardPreauthText(this.store.daysLeft() === 0 ? 'payment.card_preauth.header.today' : 'payment.card_preauth.header.tomorrow');
        return `${cardPreauthText('payment.card_preauth.header.expires')} ${day}`;
    });

    // Expired gets its own message ("check your subscription and extend it"); the last
    // days keep the card-confirmation one; the promo variant explains the headline offer.
    readonly text = computed(() => {
        if (this.promo()) return this.promoToastText();
        return cardPreauthText(this.expired() ? 'payment.card_preauth.toast.expired_text' : 'payment.card_preauth.toast.text');
    });

    /** Free days: verify the card now, charged after the free window. Bonus days: paid first —
     *  on the expiry day while a subscription runs, now without one — the free days follow. */
    private promoToastText(): string {
        const offer = this.store.promoBenefit()?.headline;
        if (offer?.type !== 'BONUSDAYS') return promoText('payment.promo.toast.text');
        const key = this.store.subscriptionLive() ? 'payment.promo.toast.text_bonus_renewal' : 'payment.promo.toast.text_bonus_buy';
        return promoText(key, [String(offer.days)]);
    }

    readonly confirmLabel = computed(() => {
        if (this.promo()) return promoText('payment.promo.toast.confirm');
        return cardPreauthText(this.expired() ? 'payment.card_preauth.toast.expired_confirm' : 'account_notice.button.confirm');
    });

    t(key: string): string {
        return cardPreauthText(key);
    }

    /** Promo copy helper for the template (eyebrow). */
    p(key: string): string {
        return promoText(key);
    }

    later(): void {
        if (this.promo()) this.store.closePromoToast();
        else this.store.closeToast();
    }

    confirm(): void {
        if (this.promo()) this.store.openPromoOffer();
        else this.store.confirmCard();
    }
}
